import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Card, Col, Container, Dropdown, Row, Spinner } from "react-bootstrap";
import { fetchAssessmentTypes, fetchReportAssessments } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { AssessmentType } from "../../types/assessmenttype";
import { AssessmentLineChart } from "../report-ui/AssessmentLineChart";
import useApp from "../../utils/useApp";
import { CSVLink } from "react-csv";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { getCurrentDateTime } from "../../helpers/date";
import AssessmentWellBeing from "./AssessmentWellBeing";
import AssessmentContinuum from "./AssessmentContinuum";

interface AssessmentTabState {
  buildPDF: any;
  canvas: any;
  setCanvas: any;
  iPad: boolean;
}

interface Option {
  key: string | number;
  name: string;
  [key: string]: unknown;
}

const AssessmentsTab = ({ buildPDF, canvas, setCanvas, iPad }: AssessmentTabState) => {
  const { auth }: any = useApp();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(10000);
  const [assessments, setAssessments] = useState<any>();
  const [assessmentHeaders, setAssessmentHeaders] = useState<any[]>();
  const [questionAverages, setQuestionAverages] = useState<any>();
  const [trend, setTrend] = useState<any>();
  const [selectedType, setSelectedType] = useState<AssessmentType>();
  const [selectedTypeQuestions, setSelectedTypeQuestions] = useState<Option[]>([]);
  const [assessmentTypes, setAssessmentTypes] = useState<AssessmentType[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<any>();
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultCSVHeaders = [
    { label: "Assessment Date", key: "assessment_at" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Assessment ID", key: "assessment_id" },
    { label: "Assessment Name", key: "assessment_name" },
    { label: "Question", key: "question" },
    { label: "Answer Scale", key: "field_set_name" },
    { label: "Value Description", key: "value_description" },
    { label: "Value", key: "value" },
  ];

  const continuumCSVHeaders = [
    { label: "Assessment Date", key: "date" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Assessment ID", key: "assessment_id" },
    { label: "Assessment Name", key: "assessment_name" },

    { label: "Composite", key: "composite" },

    { label: "Income", key: "income" },
    { label: "Income Description", key: "income_val" },

    { label: "Education", key: "education" },
    { label: "Education Description", key: "education_val" },

    { label: "Employment", key: "employment" },
    { label: "Employment Description", key: "employment_val" },

    { label: "Childcare", key: "childcare" },
    { label: "Childcare Description", key: "childcare_val" },

    { label: "Health Care", key: "healthcare" },
    { label: "Health Description", key: "healthcare_val" },

    { label: "Nutrition", key: "nutrition" },
    { label: "Nutrition Description", key: "nutrition_val" },

    { label: "Housing", key: "housing" },
    { label: "Housing Description", key: "housing_val" },

    { label: "Transportation", key: "transportation" },
    { label: "Transportation Description", key: "transportation_val" },

    { label: "Support Systems", key: "support" },
    { label: "Support Systems Description", key: "support_val" },

    { label: "Family Interactions", key: "family" },
    { label: "Family Interactions Description", key: "family_val" },
  ];

  const wellbeingCSVHeaders = [
    { label: "Assessment Date", key: "date" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Assessment ID", key: "assessment_id" },
    { label: "Assessment Name", key: "assessment_name" },

    { label: "Composite", key: "composite" },

    //Happiness and Life Satisfaction
    { label: "Question 1", key: "question1" },
    { label: "Value 1", key: "1" },
    { label: "Question 2", key: "question2" },
    { label: "Value 2", key: "2" },

    //Mental and Physical Health
    { label: "Question 3", key: "question3" },
    { label: "Value 3", key: "3" },
    { label: "Question 4", key: "question4" },
    { label: "Value 4", key: "4" },

    //Meaning and Purpose
    { label: "Question 5", key: "question5" },
    { label: "Value 5", key: "5" },
    { label: "Question 6", key: "question6" },
    { label: "Value 6", key: "6" },

    //Character and Virtue
    { label: "Question 7", key: "question7" },
    { label: "Value 7", key: "7" },
    { label: "Question 8", key: "question8" },
    { label: "Value 8", key: "8" },

    //Close Social Relationships
    { label: "Question 9", key: "question9" },
    { label: "Value 9", key: "9" },
    { label: "Question 10", key: "question10" },
    { label: "Value 10", key: "10" },

    //Financial and Material Stability
    { label: "Question 11", key: "question11" },
    { label: "Value 11", key: "11" },
    { label: "Question 12", key: "question12" },
    { label: "Value 12", key: "12" },
  ];

  useEffect(() => {
    const initialize = async () => {
      resetDates();
      const types = await getAssessmentTypes();
      const firstType = types?.flatMap((group: any) => group.options)[0].original;
      //default headers for csv export
      setAssessmentHeaders(defaultCSVHeaders);

      if (firstType) {
        switch (firstType.key) {
          case "continuum":
            setAssessmentHeaders(continuumCSVHeaders);
            break;
          case "well_being":
            setAssessmentHeaders(wellbeingCSVHeaders);
            break;
          default:
            setAssessmentHeaders(defaultCSVHeaders);
        }
        updateAssessmentType(firstType);
      }
    };
    initialize();
  }, []);

  const resetDates = async () => {
    const start = new Date();
    start.setDate(start.getDate() - 10000);
    setStartDate(start.toISOString().slice(0, 10));
    const end = new Date();
    setEndDate(end.toISOString().slice(0, 10));
    return { start, end };
  };

  const getAssessmentTypes = async () => {
    try {
      const res: any = await fetchAssessmentTypes();
      var types = res[0].data;
      var customTypes = [];
      //gets all published and archived assessment types
      types = types.filter((t: any) => t?.is_published === 1);
      types.forEach((type: any) => {
        type.question.forEach((obj: any) => {
          obj.name = obj.question; // Rename 'question' to 'name'
        });
      });
      const formatted = types.map((t: any) => ({
        value: t.key,
        label: t.name,
        original: t,
        isDisabled: false,
        showNormal: true,
      }));

      customTypes = formatted.filter((t: any) => t?.original?.organization_id == auth?.profile?.userOrgID);
      const defaultTypes = formatted.filter((t: any) => t?.original?.organization_id != auth?.profile?.userOrgID);
      const published = customTypes.filter((t: any) => !t.original.is_archived);
      const archived = customTypes.filter((t: any) => t.original.is_archived);
      const groupedTypes = [
        { label: "Default", options: defaultTypes },
        { label: "Published", options: published },
        { label: "Archived", options: archived },
      ];
      setAssessmentTypes(groupedTypes as any);
      return groupedTypes;
    } catch (err: any) {
      console.log(err);
    }
  };

  const updateAssessmentType = async (e: any) => {
    setIsLoading(true);
    const { start, end } = await resetDates();
    // save a formatted option so the dropdown shows its label when selected
    const formatted = {
      ...e,
      label: e.name,
      value: e.key,
    };
    setSelectedType(formatted as any);
    let formattedQuestions = e.question.map((q: any) => ({
      key: q.key,
      name: q.question,
    }));
    formattedQuestions.unshift({ key: "0", name: "All Questions" });
    setSelectedTypeQuestions(formattedQuestions);
    setSelectedQuestion(null);
    //used to call everything except continuum
    if (start && end && e?.key !== "continuum" && e?.key !== "well_being") {
      getAssessments(e?.id, start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
      setAssessmentHeaders(defaultCSVHeaders);
    } else {
      setIsLoading(false);
    }
  };

  const getAssessments = (assessmentTypeId: string, start: string, end: string) => {
    fetchReportAssessments(assessmentTypeId, start, end)
      .then((res: any) => {
        const assessments = res[0].data;
        setAssessments(assessments);
        calculateQuestions(assessments);
        calculateTrend(assessments);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      });
  };

  const updateQuestion = (e: any) => {
    if (e.key !== "0") {
      const question = selectedType?.question.find((q: any) => q.key === e.key);
      setSelectedQuestion(question);
      const filteredAssessments = assessments.filter((a: any) => a?.question === question?.question);
      calculateTrend(filteredAssessments);
    } else {
      setSelectedQuestion(null);
      calculateTrend(assessments);
    }
  };

  useEffect(() => {
    if (reportRunning) {
      buildPDF(selectedType?.name);
      setReportRunning(false);
    }
  }, [canvas]);

  const calculateQuestions = (data: any) => {
    const grouped = Object.values(
      data.reduce((acc: any, curr: any) => {
        const id = curr.assessmenttype_field_id;
        const value = parseFloat(curr.value); // Convert string to number

        if (!acc[id]) {
          acc[id] = {
            assessmenttype_field_id: id,
            question: curr.question,
            total: 0,
            count: 0,
          };
        }

        acc[id].total += value;
        acc[id].count += 1;

        return acc;
      }, {}),
    ).map((group: any) => ({
      assessment_type_field_id: group.assessmenttype_field_id,
      question: group.question,
      average: parseFloat((group.total / group.count).toFixed(2)),
    }));

    setQuestionAverages(grouped);
  };

  const calculateTrend = (data: any[]) => {
    const groupedData: { [date: string]: { sum: number; count: number } } = data.reduce((acc: any, obj: any) => {
      const date = obj.assessment_at;
      const value = parseFloat(obj.value); // Convert string to number

      if (!acc[date]) {
        acc[date] = { sum: 0, count: 0 };
      }
      acc[date].sum += value;
      acc[date].count++;
      return acc;
    }, {});

    // Calculate average for each group
    const averagedData = Object.entries(groupedData).map(([date, { sum, count }]) => ({
      date,
      score: sum / count,
    }));

    setTrend(averagedData);
  };

  const downloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("custom-section");
    const reportTitle = document.getElementById("custom-title");

    //continuum specific
    // const pieChart = document.getElementById("continuum-pie");
    const downloadButton = document.getElementById("download-continuum-button");
    const refreshButton = document.getElementById("refresh-continuum-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (downloadButton) downloadButton.style.visibility = "hidden";
      if (refreshButton) refreshButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      html2canvas(reportSection, {
        useCORS: true,
        scale: 2,
      }).then((canvas) => {
        setCanvas(canvas);
      });

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="custom-section">
      <Row id="custom-title" className="mb-3">
        <Col className="col-8">
          <h4 className="mb-0 app-header-text">Assessments</h4>
        </Col>
        <Col className="text-end col-4">
          {!iPad && (
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button" disabled={selectedType ? false : true}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Downloads
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="1" onClick={() => downloadReport()}>
                  Report
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="2"
                  as={CSVLink}
                  data={assessments ? assessments : [{}]}
                  headers={assessmentHeaders}
                  filename={`${selectedType?.name.replaceAll(" ", "")}_${startDate?.replaceAll("-", "")}_to_${endDate?.replaceAll(
                    "-",
                    "",
                  )}_${getCurrentDateTime()}.csv`}
                  target="_blank"
                >
                  Data Export
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
      </Row>
      <Row className="justify-content-center mb-4">
        <Col xs={8}>
          {!reportRunning && (
            <SelectObjectTypeahead
              label="Assessment"
              value={selectedType}
              options={assessmentTypes}
              onUpdate={(e: any) => updateAssessmentType(e)}
              focused={true}
              groupHeading
            />
          )}
        </Col>
      </Row>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : selectedType?.key === "well_being" ? (
        <AssessmentWellBeing assessmentType={selectedType} setCsvHeaders={setAssessmentHeaders} setCsvData={setAssessments} />
      ) : selectedType?.key === "continuum" ? (
        <AssessmentContinuum setCsvHeaders={setAssessmentHeaders} setCsvData={setAssessments} />
      ) : selectedType && trend.length > 0 ? (
        <>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={90}
            intervalOneText={"Last 3 Months"}
            intervalTwo={180}
            intervalTwoText={"Last 6 Months"}
            intervalThree={365}
            intervalThreeText={"Last Year"}
            intervalFour={10000}
            intervalFourText={"All Time"}
            onClickRefresh={getAssessments}
            hideDates={true}
            assessmentId={selectedType?.id}
          />
          <Row className="justify-content-center gap-3 mt-3">
            {selectedType?.is_archived && (
              <label className="note-type text-muted text-center">
                {"Archived: " + (selectedType?.archived_at ? new Date(selectedType.archived_at).toLocaleDateString() : "")}
              </label>
            )}
            {questionAverages?.map((q: any) => (
              <Col xs={12} md={8}>
                <Card className="bg-light">
                  <Card.Body>
                    <Row className="text-center">
                      <Col xs={9} className="d-flex align-items-center">
                        <h5 className="app-orange-text text-start mb-0">{q?.question}</h5>
                      </Col>
                      <Col xs={3} className="d-flez justify-content-center align-items-center">
                        <h2 className="text-muted">{q?.average}</h2>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <Row className="my-3">
            <Col></Col>
            <Col className="col-8">
              {reportRunning ? (
                <p className="mb-0">{selectedQuestion?.name}</p>
              ) : (
                <SelectObjectTypeahead
                  initialOption="All Questions"
                  label="Question"
                  value={selectedQuestion}
                  options={selectedTypeQuestions}
                  onUpdate={(e: any) => updateQuestion(e)}
                />
              )}
            </Col>
            <Col></Col>
          </Row>
          <Row className="mb-5">
            <Col></Col>
            <Col className="col-8">
              <AssessmentLineChart titleText={selectedType?.name} data={trend} assessType={selectedType} />
            </Col>
            <Col></Col>
          </Row>
        </>
      ) : (
        selectedType && <p className="mt-2">No Assessment Data</p>
      )}
    </div>
  );
};

export default AssessmentsTab;
